import type { WsFilePath } from '@bangle.io/ws-path';

/** Readable notes one graph answer holds at most (Folio parity). */
export const GRAPH_PAGE_LIMIT = 10_000;

export const GRAPH_MAX_DEPTH = 3;

export type GraphDirection = 'both' | 'in' | 'out';

/** Note nodes only in v1 (Folio `doc` kind stands in for markdown notes). */
export type GraphNodeKind = 'doc';

export type GraphNode = {
  id: string;
  title: string;
  kind: GraphNodeKind;
  path?: string;
  degree: number;
};

export type GraphEdge = {
  source: string;
  target: string;
  count: number;
};

export type GraphResponse = {
  nodes: GraphNode[];
  edges: GraphEdge[];
  totalPages: number;
  truncated: boolean;
  limit: number;
  center?: string;
};

/**
 * Forward adjacency collected while scanning note bodies: each source maps to
 * resolved target wsPaths (duplicates preserved for edge `count`).
 */
export type NoteLinkAdjacency = ReadonlyMap<string, readonly string[]>;

export type NoteLinkGraph = {
  /** Notes in the workspace (or truncated subset). */
  notePaths: readonly WsFilePath[];
  /** Outgoing links per source wsPath. */
  outgoing: NoteLinkAdjacency;
  truncated: boolean;
  limit: number;
};

export function clampDepth(raw: unknown): number {
  const n =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim() !== ''
        ? Number(raw)
        : Number.NaN;
  if (!Number.isFinite(n)) {
    return 1;
  }
  return Math.min(GRAPH_MAX_DEPTH, Math.max(1, Math.floor(n)));
}

export function parseDirection(raw: unknown): GraphDirection {
  return raw === 'in' || raw === 'out' ? raw : 'both';
}

function titleForPath(path: WsFilePath): string {
  return path.fileNameWithoutExtension || path.fileName || path.path;
}

function computeDegrees(
  nodeIds: readonly string[],
  edges: readonly GraphEdge[],
): Map<string, number> {
  const neighbours = new Map<string, Set<string>>();
  const link = (a: string, b: string) => {
    let set = neighbours.get(a);
    if (!set) {
      neighbours.set(a, (set = new Set()));
    }
    set.add(b);
  };
  for (const e of edges) {
    link(e.source, e.target);
    link(e.target, e.source);
  }
  const degrees = new Map<string, number>();
  for (const id of nodeIds) {
    degrees.set(id, neighbours.get(id)?.size ?? 0);
  }
  return degrees;
}

function edgesAmong(
  ids: ReadonlySet<string>,
  outgoing: NoteLinkAdjacency,
): GraphEdge[] {
  const counts = new Map<string, number>();
  for (const source of ids) {
    const targets = outgoing.get(source) ?? [];
    for (const target of targets) {
      if (!ids.has(target) || source === target) {
        continue;
      }
      const key = `${source}\0${target}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  const edges: GraphEdge[] = [];
  for (const [key, count] of counts) {
    const sep = key.indexOf('\0');
    edges.push({
      source: key.slice(0, sep),
      target: key.slice(sep + 1),
      count,
    });
  }
  return edges;
}

/**
 * Builds a Folio-shaped GraphResponse for the whole workspace (or truncated
 * note set). Orphans are included; the UI may filter them.
 */
export function toGraphResponse(graph: NoteLinkGraph): GraphResponse {
  const { notePaths, outgoing, truncated, limit } = graph;
  const ids = notePaths.map((p) => p.wsPath);
  const idSet = new Set(ids);
  const edges = edgesAmong(idSet, outgoing);
  const degrees = computeDegrees(ids, edges);
  const nodes: GraphNode[] = notePaths.map((path) => ({
    id: path.wsPath,
    title: titleForPath(path),
    kind: 'doc',
    path: path.path,
    degree: degrees.get(path.wsPath) ?? 0,
  }));
  return {
    nodes,
    edges,
    totalPages: notePaths.length,
    truncated,
    limit,
  };
}

function neighboursOf(
  frontier: readonly string[],
  direction: GraphDirection,
  outgoing: NoteLinkAdjacency,
  incoming: ReadonlyMap<string, readonly string[]>,
): string[] {
  const found: string[] = [];
  for (const id of frontier) {
    if (direction !== 'in') {
      for (const t of outgoing.get(id) ?? []) {
        found.push(t);
      }
    }
    if (direction !== 'out') {
      for (const s of incoming.get(id) ?? []) {
        found.push(s);
      }
    }
  }
  return found;
}

function invertOutgoing(
  outgoing: NoteLinkAdjacency,
): Map<string, string[]> {
  const incoming = new Map<string, string[]>();
  for (const [source, targets] of outgoing) {
    for (const target of targets) {
      const list = incoming.get(target);
      if (list) {
        list.push(source);
      } else {
        incoming.set(target, [source]);
      }
    }
  }
  return incoming;
}

/**
 * Local neighbourhood of `centerWsPath` within `depth` hops (Folio semantics,
 * client-side). Edges are every link among the kept nodes.
 */
export function buildLocalGraph(
  graph: NoteLinkGraph,
  centerWsPath: string,
  opts: {
    depth: number;
    direction: GraphDirection;
    limit?: number;
  },
): GraphResponse {
  const limit = opts.limit ?? GRAPH_PAGE_LIMIT;
  const depth = clampDepth(opts.depth);
  const direction = parseDirection(opts.direction);
  const byWsPath = new Map(graph.notePaths.map((p) => [p.wsPath, p]));
  if (!byWsPath.has(centerWsPath)) {
    return {
      nodes: [],
      edges: [],
      totalPages: 0,
      truncated: false,
      limit,
      center: centerWsPath,
    };
  }

  const incoming = invertOutgoing(graph.outgoing);
  const order: string[] = [centerWsPath];
  const known = new Set(order);
  let frontier = [centerWsPath];
  for (
    let level = 0;
    level < depth && frontier.length > 0 && known.size < limit + 1;
    level++
  ) {
    const next: string[] = [];
    for (const id of neighboursOf(
      frontier,
      direction,
      graph.outgoing,
      incoming,
    )) {
      if (known.has(id) || !byWsPath.has(id)) {
        continue;
      }
      known.add(id);
      order.push(id);
      next.push(id);
    }
    frontier = next;
  }

  const totalPages = order.length;
  const kept = order.slice(0, limit);
  const keptSet = new Set(kept);
  const edges = edgesAmong(keptSet, graph.outgoing);
  const degrees = computeDegrees(kept, edges);
  const nodes: GraphNode[] = kept.map((id) => {
    const path = byWsPath.get(id)!;
    return {
      id,
      title: titleForPath(path),
      kind: 'doc',
      path: path.path,
      degree: degrees.get(id) ?? 0,
    };
  });

  return {
    nodes,
    edges,
    totalPages,
    truncated: totalPages > kept.length,
    limit,
    center: centerWsPath,
  };
}

/**
 * Truncates note list to `limit` (listed order) and filters outgoing edges to
 * kept ids. Used when building `$noteLinkGraph` from a full workspace scan.
 */
export function assembleNoteLinkGraph({
  notePaths,
  outgoing,
  limit = GRAPH_PAGE_LIMIT,
}: {
  notePaths: readonly WsFilePath[];
  outgoing: NoteLinkAdjacency;
  limit?: number;
}): NoteLinkGraph {
  const truncated = notePaths.length > limit;
  const kept = truncated ? notePaths.slice(0, limit) : notePaths;
  const keptIds = new Set(kept.map((p) => p.wsPath));
  const filtered = new Map<string, readonly string[]>();
  for (const path of kept) {
    const targets = (outgoing.get(path.wsPath) ?? []).filter((t) =>
      keptIds.has(t),
    );
    if (targets.length > 0) {
      filtered.set(path.wsPath, targets);
    }
  }
  return {
    notePaths: kept,
    outgoing: filtered,
    truncated,
    limit,
  };
}
